import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { propertyAPI, appointmentAPI, getUploadUrl } from '../../lib/api';
import api from '../../lib/api';
import { formatCurrency, formatCommissionRate } from '../../lib/utils';
import { GlassCard, StatusBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  MapPin, Building2, Users, Calendar as CalendarIcon,
  ArrowLeft, Home, DollarSign, Check, Sparkles, UserCheck, Clock,
} from 'lucide-react';
import PropertyMap from '../../components/common/PropertyMap';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formDate, setFormDate] = useState(null);
  const [formTime, setFormTime] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Task 2 (V4.18) — list of HH:MM slots already booked by an APPROVED
  // appointment for the selected date. Pending/rescheduled appointments do
  // NOT appear here so the customer can still pick the same time.
  const [blockedTimes, setBlockedTimes] = useState([]);

  // Refresh blocked slots whenever the date changes
  useEffect(() => {
    if (!formDate || !id) { setBlockedTimes([]); return; }
    let cancelled = false;
    const dateStr = format(formDate, 'yyyy-MM-dd');
    api.get('/appointments/blocked-slots', { params: { property_id: id, date: dateStr } })
      .then((res) => {
        if (cancelled) return;
        setBlockedTimes(res.data?.blocked_times || []);
        // If current selected time is now blocked, clear it
        if (formTime && (res.data?.blocked_times || []).includes(formTime)) {
          setFormTime('');
        }
      })
      .catch(() => { if (!cancelled) setBlockedTimes([]); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDate, id]);

  const loadData = useCallback(async () => {
    try {
      const propRes = await propertyAPI.getProperty(id);
      setProperty(propRes.data);
      const availRes = await propertyAPI.getAvailability(id);
      setUnits(availRes.data.units || []);
      setStats(availRes.data.stats || {});
    } catch (error) {
      toast.error('Failed to load property');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async () => {
    if (!formDate || !formTime) {
      toast.error('Please select date and time');
      return;
    }
    setSubmitting(true);
    try {
      const response = await appointmentAPI.createAppointment({
        property_id: id,
        preferred_date: format(formDate, 'yyyy-MM-dd'),
        preferred_time: formTime,
        notes: formNotes,
      });
      
      // Show appropriate message based on assignment
      if (response.data?.lead_status === 'open') {
        toast.success('Viewing request submitted! We are matching you with a qualified agent.');
      } else {
        toast.success('Viewing request submitted! Your agent will contact you shortly.');
      }
      
      setShowModal(false);
      setFormDate(null); setFormTime(''); setFormNotes('');
      navigate('/my-appointments');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const timeSlots = ['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00', '18:00'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  if (!property) return null;

  return (
    <div className="space-y-6" data-testid="property-detail">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4 mr-2" />Back
      </Button>

      {/* Hero */}
      <div className="relative h-64 md:h-80 rounded-2xl overflow-hidden bg-gray-200">
        {property.images?.[0] && (
          <img src={getUploadUrl(property.images[0])} alt={property.name} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="px-3 py-1 rounded-lg text-xs font-medium bg-[#4E50FF] text-white capitalize mb-3 inline-block">
            {property.property_type}
          </span>
          <h1 className="text-3xl font-bold text-white mb-2">{property.name}</h1>
          <div className="flex items-center gap-1 text-gray-200">
            <MapPin className="w-4 h-4" />{property.location}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Address & Map */}
          {property.address && (
            <GlassCard>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-red-400" />Location
              </h2>
              <p className="text-gray-600 mb-4" data-testid="property-address">{property.address}</p>
              <PropertyMap
                address={property.address}
                propertyName={property.name}
                latitude={property.latitude}
                longitude={property.longitude}
                height="300px"
              />
            </GlassCard>
          )}

          <GlassCard>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Overview</h2>
            <p className="text-gray-600 mb-6">{property.description}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 rounded-xl bg-gray-50 border border-gray-100">
                <Building2 className="w-6 h-6 text-[#4E50FF] mx-auto mb-2" />
                <p className="text-lg font-semibold text-gray-900">{property.total_units}</p>
                <p className="text-xs text-gray-500">Total Units</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-gray-50 border border-gray-100">
                <Home className="w-6 h-6 text-[#4E50FF] mx-auto mb-2" />
                <p className="text-lg font-semibold text-gray-900 capitalize">{property.property_type}</p>
                <p className="text-xs text-gray-500">Type</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-gray-50 border border-gray-100">
                <Users className="w-6 h-6 text-[#4E50FF] mx-auto mb-2" />
                <p className="text-lg font-semibold text-gray-900 truncate">{property.developer}</p>
                <p className="text-xs text-gray-500">Developer</p>
              </div>
              {property.commission_percentage != null && (
                <div className="text-center p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                  <DollarSign className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                  <p className="text-lg font-semibold text-emerald-700">{formatCommissionRate(property)}</p>
                  <p className="text-xs text-gray-500">Commission</p>
                </div>
              )}
            </div>
          </GlassCard>

          <GlassCard>
            <Tabs defaultValue="overview">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Unit Availability</h2>
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="units">All Units</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                    <p className="text-2xl font-bold text-emerald-700">{stats.available || 0}</p>
                    <p className="text-sm text-gray-500">Available</p>
                  </div>
                  <div className="p-4 rounded-xl bg-amber-50 border border-amber-100">
                    <p className="text-2xl font-bold text-amber-700">{stats.reserved || 0}</p>
                    <p className="text-sm text-gray-500">Reserved</p>
                  </div>
                  <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
                    <p className="text-2xl font-bold text-blue-700">{stats.booked || 0}</p>
                    <p className="text-sm text-gray-500">Booked</p>
                  </div>
                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
                    <p className="text-2xl font-bold text-gray-700">{stats.sold || 0}</p>
                    <p className="text-sm text-gray-500">Sold</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="units">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Unit</TableHead>
                        <TableHead>Floor</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Size</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {units.length > 0 ? units.map((unit) => (
                        <TableRow key={unit.id}>
                          <TableCell className="font-medium">{unit.unit_number}</TableCell>
                          <TableCell>{unit.floor}</TableCell>
                          <TableCell>{unit.unit_type}</TableCell>
                          <TableCell>{unit.size_sqft} sqft</TableCell>
                          <TableCell className="text-[#4E50FF] font-mono">{formatCurrency(unit.price)}</TableCell>
                          <TableCell><StatusBadge status={unit.status} /></TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-gray-400 py-8">No units available</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>
          </GlassCard>

          {property.amenities && property.amenities.length > 0 && (
            <GlassCard>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Amenities</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {property.amenities.map((amenity, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-gray-600">
                    <Check className="w-4 h-4 text-emerald-500" />{amenity}
                  </div>
                ))}
              </div>
            </GlassCard>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <GlassCard className="sticky top-6">
            <div className="text-center mb-6">
              <p className="text-sm text-gray-500 mb-1">Price Range</p>
              <p className="text-2xl font-bold text-[#4E50FF] font-mono">
                {formatCurrency(property.price_range_min)}
              </p>
              <p className="text-gray-400">to</p>
              <p className="text-2xl font-bold text-[#4E50FF] font-mono">
                {formatCurrency(property.price_range_max)}
              </p>
            </div>

            {user?.role === 'agent' ? (
              // V4.18.43 — Hide the Create Booking CTA when the property
              // has zero available units. `stats.available` comes from
              // GET /properties/{id}/availability. Falls back to true
              // (show button) if stats aren't loaded yet, so the button
              // doesn't flicker in.
              Number(stats?.available ?? 1) > 0 ? (
                <Button
                  onClick={() => {
                    // Use window.location to avoid Google Maps interfering with React Router navigation
                    window.location.href = `/bookings/create?property_id=${property.id}`;
                  }}
                  className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white"
                  data-testid="create-booking-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />Create Booking
                </Button>
              ) : (
                <div
                  className="w-full rounded-md bg-gray-100 text-gray-500 text-center py-2 text-sm"
                  data-testid="no-units-available-message"
                >
                  No units available for booking
                </div>
              )
            ) : user ? (
              <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="book-appointment-btn">
                    <CalendarIcon className="w-4 h-4 mr-2" />Book Viewing
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle>Book a Viewing</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {/* Agent Assignment Info */}
                    <div className={`p-4 rounded-xl ${user?.upline_id ? 'bg-emerald-50 border border-emerald-200' : 'bg-blue-50 border border-blue-200'}`}>
                      {user?.upline_id ? (
                        <div className="flex items-start gap-3">
                          <UserCheck className="w-5 h-5 text-emerald-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-emerald-800">Your Agent Will Handle This</p>
                            <p className="text-xs text-emerald-600 mt-1">Your viewing request will be sent to your assigned agent.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-3">
                          <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-blue-800">We'll Match You With an Agent</p>
                            <p className="text-xs text-blue-600 mt-1">A qualified agent will be assigned to assist you with this viewing.</p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Property Info */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Property</p>
                      <p className="font-medium text-gray-900">{property?.name}</p>
                      <p className="text-sm text-gray-500">{property?.location}</p>
                    </div>
                    
                    <div>
                      <Label htmlFor="preferred-date">Preferred Date *</Label>
                      <Input
                        id="preferred-date"
                        type="date"
                        data-testid="select-date"
                        className="mt-1 w-full"
                        min={format(new Date(), 'yyyy-MM-dd')}
                        value={formDate ? format(formDate, 'yyyy-MM-dd') : ''}
                        onChange={(e) => setFormDate(e.target.value ? new Date(e.target.value + 'T00:00:00') : null)}
                      />
                    </div>
                    <div>
                      <Label>Preferred Time *</Label>
                      <Select value={formTime} onValueChange={setFormTime}>
                        <SelectTrigger data-testid="select-time"><SelectValue placeholder="Select time" /></SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => {
                            const isBlocked = blockedTimes.includes(time);
                            return (
                              <SelectItem
                                key={time}
                                value={time}
                                disabled={isBlocked}
                                data-testid={`time-slot-${time}${isBlocked ? '-blocked' : ''}`}
                              >
                                {time}{isBlocked ? ' — Booked' : ''}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      {blockedTimes.length > 0 && (
                        <p className="text-[11px] text-gray-400 mt-1">Greyed-out times are already booked.</p>
                      )}
                    </div>
                    <div>
                      <Label>Notes (Optional)</Label>
                      <Textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} placeholder="Any special requests or questions..." />
                    </div>
                    <Button 
                      onClick={handleSubmit} 
                      disabled={submitting || !formDate || !formTime}
                      className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white" 
                      data-testid="submit-appointment"
                    >
                      {submitting ? 'Submitting...' : 'Request Viewing'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            ) : (
              <Button onClick={() => navigate('/login')} className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white">
                Login to Book
              </Button>
            )}

            {property.commission_percentage != null && (
              <div className="mt-4 p-3 bg-emerald-50 rounded-xl text-center">
                <p className="text-xs text-gray-500">Agent Commission</p>
                <p className="text-lg font-bold text-emerald-700">{formatCommissionRate(property)}</p>
              </div>
            )}
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
